import { CdkScrollable } from "@angular/cdk/scrolling";
import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { FlexDirective, LayoutAlignDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandBarComponent, LabelCommand } from "@tylertech/commands-module";
import { DocumentSearchCriteria, ExecutionResult, IAttachment, SaveAttachmentViewModel, } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { AttachmentModel } from "../../interfaces";
import { AttachmentService, DocumentService } from "../../services";
import { AttachmentsContainerComponent } from "../attachmentContainer";

@Component({
    selector: "attachment-viewer",
    templateUrl: "./attachmentViewer.tmpl.html",
    imports: [
        AttachmentsContainerComponent,
        CdkScrollable,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
    ]
})
export class AttachmentViewerComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef);
    private readonly $attachmentService = inject(AttachmentService);
    private readonly $documentApiService = inject(DocumentService);


    @Input() readonly docType?: string;
    @Input() readonly entityId: string;
    @Input() readonly entityType: string;
    @Input() readonly isAdd: boolean;
    @Input() localAttachments: Array<IAttachment>;
    @Input() readonly canEdit: boolean;

    private readonly destructor$ = new Subject<void>();

    readonly commands: ReadonlyArray<Command>;

    attachments: Array<AttachmentModel> = [];

    protected attachmentsToDelete: Array<AttachmentModel> = [];
    protected attachmentsToSave: Array<AttachmentModel> = [];
    isLoading: boolean;

    constructor()
    {
        this.commands = [
            new LabelCommand({
                type: "outlined",
                label: () => this.cancelLabel,
                action: () =>
                {
                    this.attachments = this.attachments.concat(this.attachmentsToDelete);

                    this.removeUnSavedAttachments();

                    this.onClose();
                },
                getVisible: () => this.canEdit
            }),
            new LabelCommand({
                type: "outlined",
                label: () => this.saveLabel,
                action: () =>
                {
                    return this.save();
                },
                getVisible: () => this.canEdit
            }),
            new LabelCommand({
                type: "raised",
                label: () => this.closeLabel,
                action: () =>
                {
                    this.onClose();
                },
                getVisible: () => !this.canEdit
            }),
        ];
    }

    get maxFileSize(): number
    {
        return this.$attachmentService.MaxFileSize;
    }

    get supportedFileTypes(): string
    {
        return this.$attachmentService.getSupportedFileTypes().join(", ");
    }

    get cancelLabel(): string { return "Cancel"; }
    get saveLabel(): string { return "Save & Close"; }
    get closeLabel(): string { return "Close"; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (!this.entityId)
        {
            this.attachments = this.localAttachments;
        }

        if (!this.isAdd)
        {
            if (this.entityId)
            {
                this.getAttachments();
            }
        }
    }

    private getAttachments(): void
    {
        this.isLoading = true;
        const criteria: DocumentSearchCriteria = {
            EntityId: this.entityId,
            EntityType: this.entityType,
        };

        this.$documentApiService.GetDocumentList(criteria)
            .then(
                (result: ExecutionResult<Array<AttachmentModel>>) =>
                {
                    this.attachments = result.Result;
                })
            .finally(() =>
            {
                this.isLoading = false;
            });
    }

    addAttachment(attachmentsToAdd: Array<AttachmentModel>): void
    {
        if (!this.attachments)
        {
            this.attachments = [];
        }

        this.attachments = this.attachments.concat(attachmentsToAdd);
        this.attachmentsToSave = this.attachmentsToSave.concat(attachmentsToAdd);
        this.isLoading = false;
    }

    onDelete(attachment: AttachmentModel): void
    {
        attachment.IsDeleted = true;

        this.attachments = this.attachments.filter(x => x !== attachment);

        if (attachment.Id)
        {
            this.attachmentsToDelete.push(attachment);
        }
    }

    private removeUnSavedAttachments(): void
    {
        for (const attachment of this.attachmentsToSave)
        {
            this.attachments = this.attachments.filter(x => x !== attachment);
        }
    }

    private save(): void
    {
        this.executeDelete();
        this.executeSave();
    }

    private executeDelete(): void
    {
        for (const attachment of this.attachmentsToDelete)
        {
            if (this.entityId)
            {
                const criteria: DocumentSearchCriteria = {
                    EntityId: attachment.Id,
                    EntityType: this.entityType,
                };
                // TODO - why aren't we waiting on these promises?
                this.$documentApiService.DeleteAttachment(criteria);
                continue;
            }

            this.localAttachments.forEach(
                value =>
                {
                    value.IsDeleted = value.Id === attachment.Id;
                });
        }

        this.attachmentsToSave = this.attachmentsToSave.filter(x => !x.IsDeleted);
    }

    private executeSave(): void
    {
        if (!this.entityId)
        {
            this.localAttachments = this.attachments;

            this.dialogRef.close(this.attachments);
            return;
        }

        if (this.docType)
        {
            this.attachmentsToSave.forEach(
                attachment =>
                {
                    attachment.Filename = `${this.docType} - ${attachment.Filename}`;
                });
        }

        const criteria: SaveAttachmentViewModel = {
            Attachment: this.attachmentsToSave,
            EntityId: this.entityId,
            EntityType: this.entityType,
            EntityIdToAddList: [],
        };

        this.$documentApiService.AddAttachment(criteria)
            .then((_result: ExecutionResult<Array<string>>) =>
            {
                this.dialogRef.close(this.attachments);
            });

    }

    onClose(): void
    {
        this.dialogRef.close(this.attachments);
    }
}
